import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { Match2RewardsPanel } from 'src/game/minigame/match2-timestep/components/Match2RewardsPanel';
import type { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';

/** Placement of the reward bubble relative to its chest anchor. */
export type CardsRewardTooltipPosition = 'above' | 'below';

type CardsRewardTooltipOpts = ViewOpts & {
  position?: CardsRewardTooltipPosition;
};

/**
 * Displays card rewards in a temporary speech bubble anchored to a chest.
 */
export class CardsRewardTooltip extends View {
  private readonly rewards: Match2RewardsPanel;
  private animationId = 0;

  constructor(opts: CardsRewardTooltipOpts) {
    const width = opts.width ?? 270;
    const height = opts.height ?? 150;
    const position = opts.position ?? 'above';

    super({
      ...opts,
      width,
      height,
      canHandleEvents: false,
      visible: false,
    });

    const image = new ImageScaleView({
      superview: this,
      image: 'assets/ui/cards/common/speech_bubble.png',
      width,
      height,
      x: width * (0.5 - 0.8),
      y: position === 'above' ? -height * 0.5 : height * 0.5,
      scaleMethod: '9slice',
      imgScale: DOWNSCALE_FACTOR,
      sourceSlices: {
        horizontal: { left: 80, right: 80 },
        vertical: { top: 80, bottom: 80 },
      },
      centerOnOrigin: true,
      canHandleEvents: false,
    });

    new ImageScaleView({
      superview: this,
      image: 'assets/ui/cards/common/speech_arrow.png',
      width: downscale(74),
      height: downscale(47),
      y: position === 'above' ? -2 : -6,
      r: position === 'above' ? 0 : Math.PI,
      scaleMethod: 'contain',
      centerOnOrigin: true,
      canHandleEvents: false,
    });

    this.rewards = new Match2RewardsPanel({
      superview: image,
      width: width - 20,
      height: height - 20,
      x: width * 0.5,
      y: height * 0.5 - 2,
      itemSize: 65,
      itemPadding: 4,
      itemLabelSize: 22,
      itemOffsetY: 10,
      itemLabelOffsetY: -5,
      noBg: true,
      canHandleEvents: false,
    });
  }

  /**
   * Shows the supplied rewards briefly before dismissing the tooltip.
   */
  public async showAnimated(rewards: Match2Reward[]) {
    const animationId = ++this.animationId;
    this.rewards.setRewards(rewards);

    // Restart the animation when the chest is tapped again.
    animate(this).clear();
    this.updateOpts({
      scale: 0,
      visible: true,
      anchorX: 0,
      anchorY: 0,
    });

    // Reveal, hold, and dismiss the tooltip.
    await animate(this).now({ scale: 1 }, 300, animate.easeOutBack).toPromise();
    if (animationId !== this.animationId) return;

    await animate(this).now({ scale: 1 }, 1300).toPromise();
    if (animationId !== this.animationId) return;

    await animate(this).now({ scale: 0 }, 200, animate.easeInBack).toPromise();
    if (animationId !== this.animationId) return;

    this.updateOpts({ visible: false });
  }

  /**
   * Immediately stops and hides the tooltip.
   */
  public hideTooltip() {
    this.animationId += 1;
    animate(this).clear();
    this.updateOpts({ visible: false, scale: 0 });
  }
}
